import React, { useEffect, useMemo, useState } from 'react';
import trainingApi from '../api';
import { CheckCircle2, XCircle, MinusCircle, Download } from 'lucide-react';
import { nativeLabelFor } from '../i18n/strings';

const csvEscape = (value) => {
  if (value === null || value === undefined) return '';
  const s = String(value);
  if (s.includes(',') || s.includes('\n') || s.includes('"')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

const CompletionMatrix = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await trainingApi.adminMetricsMatrix();
        setData(res);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const cellByUserTraining = useMemo(() => {
    if (!data) return {};
    const map = {};
    for (const c of data.cells) map[`${c.user_id}-${c.training_id}`] = c;
    return map;
  }, [data]);

  // Sort users by % of trainings passed (desc), then by name.
  const sortedUsers = useMemo(() => {
    if (!data) return [];
    const totalTrainings = data.trainings.length || 1;
    const score = (u) => {
      let passed = 0;
      let started = 0;
      for (const t of data.trainings) {
        const c = cellByUserTraining[`${u.id}-${t.id}`];
        if (!c) continue;
        if (c.best_score !== null) passed += 1;
        if (c.attempts > 0) started += 1;
      }
      // primary: passed pct; secondary: started pct (to keep in-progress users above never-started)
      return passed * 1000 + started;
    };
    return [...data.users].sort((a, b) => {
      const s = score(b) - score(a);
      if (s !== 0) return s;
      return (a.full_name || a.username || '').localeCompare(b.full_name || b.username || '');
    });
    // include totalTrainings to silence eslint exhaustive-deps and to be obvious about the dep
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, cellByUserTraining]);

  const handleExportCsv = async () => {
    setExportMessage('');
    setExporting(true);
    try {
      const rows = await trainingApi.adminMetricsCompletions();
      if (!rows || rows.length === 0) {
        setExportMessage('No completions to export.');
        return;
      }

      const trainingCols = data.trainings;

      const byUser = new Map();
      for (const r of rows) {
        let entry = byUser.get(r.user_id);
        if (!entry) {
          entry = {
            full_name: r.full_name || '',
            username: r.username || '',
            dates: {},
            languages: {},
          };
          byUser.set(r.user_id, entry);
        }
        if (r.first_passed_at) entry.dates[r.training_id] = r.first_passed_at;
        if (r.languages) entry.languages[r.training_id] = r.languages;
      }

      const userRows = Array.from(byUser.values()).sort((a, b) =>
        (a.full_name || a.username).localeCompare(b.full_name || b.username)
      );

      // One date column per training as before, each followed by the language the
      // person actually trained in — the information that used to be implied by which
      // of the three duplicate courses they appeared under.
      const headers = [
        'Full Name',
        'Username',
        ...trainingCols.flatMap((t) => [t.title, `${t.title} — Language`]),
      ];
      const csvRows = userRows.map((u) => {
        const cells = [csvEscape(u.full_name), csvEscape(u.username)];
        for (const t of trainingCols) {
          const ts = u.dates[t.id];
          cells.push(csvEscape(ts ? new Date(ts).toLocaleString() : ''));
          const langs = u.languages[t.id];
          cells.push(csvEscape(
            langs ? langs.split(',').map((c) => nativeLabelFor(c)).join(' / ') : ''
          ));
        }
        return cells.join(',');
      });
      const csvContent = [headers.map(csvEscape).join(','), ...csvRows].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      const dateStr = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `training_completions_${dateStr}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      setExportMessage(e.message || 'Export failed.');
    } finally {
      setExporting(false);
    }
  };

  if (loading) return <div className="p-8 text-slate-400">Loading…</div>;
  if (error) return <div className="p-8 text-rose-300">{error}</div>;
  if (!data) return null;

  const { trainings } = data;

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Completion Matrix</h1>
        <div className="flex items-center gap-3">
          {exportMessage && (
            <span className="text-xs text-slate-400">{exportMessage}</span>
          )}
          <button
            type="button"
            onClick={handleExportCsv}
            disabled={exporting}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-emerald-600 hover:bg-emerald-500 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors"
            title="Download a CSV of every successful training completion"
          >
            <Download className="w-4 h-4" />
            {exporting ? 'Exporting…' : 'Export Completion CSV'}
          </button>
        </div>
      </div>
      {trainings.length === 0 ? (
        <div className="text-slate-400">No published trainings.</div>
      ) : (
        <div className="overflow-auto rounded-lg border border-slate-700">
          <table className="text-sm">
            <thead className="bg-slate-800 text-slate-300">
              <tr>
                <th className="sticky left-0 bg-slate-800 z-10 text-left px-3 py-2 border-r border-slate-700">User</th>
                {trainings.map((t) => (
                  <th key={t.id} className="px-3 py-2 text-left font-medium min-w-[140px]">
                    <div>{t.title}</div>
                    {/* One column per training now, whatever language it was taken in. */}
                    {(t.languages || []).length > 1 && (
                      <div className="text-xs font-normal text-slate-500 mt-0.5">
                        {t.languages.map((c) => nativeLabelFor(c)).join(' · ')}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedUsers.map((u) => (
                <tr key={u.id} className="border-t border-slate-700">
                  <td className="sticky left-0 bg-slate-900 z-10 px-3 py-2 border-r border-slate-700 whitespace-nowrap">
                    <div className="font-medium">{u.full_name || u.username}</div>
                    <div className="text-xs text-slate-400">{u.username}</div>
                  </td>
                  {trainings.map((t) => {
                    const c = cellByUserTraining[`${u.id}-${t.id}`];
                    return (
                      <td key={t.id} className="px-3 py-2 align-middle">
                        {!c ? (
                          <div className="flex items-center gap-1 text-slate-500">
                            <MinusCircle className="w-4 h-4" />
                            <span className="text-xs">—</span>
                          </div>
                        ) : c.best_score !== null ? (
                          <div
                            className="flex items-center gap-1 text-emerald-300"
                            title={c.languages
                              ? `Taken in ${c.languages.split(',').map((x) => nativeLabelFor(x)).join(', ')}`
                              : undefined}
                          >
                            <CheckCircle2 className="w-4 h-4" />
                            <span className="text-xs">{Number(c.best_score).toFixed(0)}%</span>
                            {c.languages && (
                              <span className="text-[10px] text-slate-500 uppercase">
                                {c.languages.split(',').join('/')}
                              </span>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 text-rose-300">
                            <XCircle className="w-4 h-4" />
                            <span className="text-xs">{c.attempts} att.</span>
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CompletionMatrix;
